import { useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { ChipSelector } from '@/components/common/Chip'
import { RuletaWheel } from './components/RuletaWheel'
import { BettingBoard } from './components/BettingBoard'
import { LastNumbers } from './components/LastNumbers'
import { useRuletaBets } from './hooks/useRuletaBets'
import { useRuletaSpin } from './hooks/useRuletaSpin'
import { getNumberColor, calculateTotalWinnings, type RuletaBet } from './utils/ruletaLogic'
import { useAuth } from '@/features/auth/contexts/AuthContext'
import { cn } from '@/lib/utils'

export function Ruleta() {
  const { user } = useAuth()
  const lastBetsRef = useRef<RuletaBet[]>([])
  const [showResult, setShowResult] = useState(false)

  const {
    bets,
    selectedChip,
    totalBet,
    selectChip,
    placeStraightBet,
    placeSplitBet,
    placeStreetBet,
    placeCornerBet,
    placeLineBet,
    placeColumnBet,
    placeDozenBet,
    placeOutsideBet,
    clearBets,
    repeatLastBets,
    doubleBets,
  } = useRuletaBets()

  const {
    phase,
    winningNumber,
    lastNumbers,
    wheelRotation,
    lastWinnings,
    isSpinning,
    spin,
    reset,
  } = useRuletaSpin()

  const isBetting = phase === 'betting'
  const isResult = phase === 'result'

  const handleSpin = async () => {
    if (bets.length === 0) return
    lastBetsRef.current = [...bets]
    await spin(bets)
    setShowResult(true)
  }

  const handleNewRound = () => {
    setShowResult(false)
    clearBets()
    reset()
  }

  const handleRepeat = () => {
    if (lastBetsRef.current.length > 0) {
      repeatLastBets(lastBetsRef.current)
    }
  }

  const winningBets =
    winningNumber !== null
      ? bets.filter((b) => b.numbers.includes(winningNumber))
      : []

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-casino-black to-casino-black-light overflow-y-auto">
      {/* Top section: Wheel and history */}
      <div className="flex flex-col lg:flex-row items-center justify-center gap-4 p-4">
        {/* Wheel */}
        <div className="flex-shrink-0">
          <RuletaWheel
            rotation={wheelRotation}
            isSpinning={isSpinning}
            winningNumber={isResult ? winningNumber : null}
          />
        </div>

        {/* Last numbers */}
        <div className="lg:ml-8">
          <LastNumbers numbers={lastNumbers} />
        </div>
      </div>

      {/* Betting board */}
      <div className="flex-1 flex flex-col items-center px-4 pb-4">
        <BettingBoard
          bets={bets}
          disabled={!isBetting || isSpinning}
          onNumberClick={placeStraightBet}
          onSplitClick={placeSplitBet}
          onStreetClick={placeStreetBet}
          onCornerClick={placeCornerBet}
          onLineClick={placeLineBet}
          onColumnClick={placeColumnBet}
          onDozenClick={placeDozenBet}
          onOutsideClick={placeOutsideBet}
        />

        {/* Controls */}
        <div className="w-full max-w-2xl mt-4 space-y-4">
          {/* Chip selector */}
          {isBetting && (
            <ChipSelector
              selectedValue={selectedChip}
              onSelect={selectChip}
              disabled={isSpinning}
            />
          )}

          {/* Bet info and actions */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-casino-black/50 rounded-xl p-4">
            {/* Bet amount */}
            <div className="text-center sm:text-left">
              <span className="text-casino-gold/60 font-cinzel text-sm">APUESTA TOTAL: </span>
              <span className="text-2xl font-bold text-casino-gold">
                ${totalBet.toLocaleString()}
              </span>
            </div>

            {/* Action buttons */}
            <div className="flex gap-2 flex-wrap justify-center">
              {isBetting && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearBets}
                    disabled={bets.length === 0 || isSpinning}
                  >
                    LIMPIAR
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRepeat}
                    disabled={lastBetsRef.current.length === 0 || isSpinning}
                  >
                    REPETIR
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={doubleBets}
                    disabled={
                      bets.length === 0 ||
                      isSpinning ||
                      !user ||
                      totalBet * 2 > user.balance
                    }
                  >
                    x2
                  </Button>
                  <Button
                    variant="gold"
                    onClick={handleSpin}
                    disabled={bets.length === 0 || isSpinning}
                    className="min-w-[120px]"
                  >
                    {isSpinning ? 'GIRANDO...' : 'GIRAR'}
                  </Button>
                </>
              )}

              {isResult && (
                <Button variant="gold" onClick={handleNewRound} className="min-w-[150px]">
                  NUEVA RONDA
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Result overlay */}
      <AnimatePresence>
        {showResult && winningNumber !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
            onClick={handleNewRound}
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              transition={{ type: 'spring', damping: 15 }}
              className="text-center p-8 max-w-md mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Winning number */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring' }}
                className={cn(
                  'w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6',
                  'text-4xl font-bold text-white shadow-lg border-4 border-casino-gold',
                  getNumberColor(winningNumber) === 'red' && 'bg-red-700',
                  getNumberColor(winningNumber) === 'black' && 'bg-gray-900',
                  getNumberColor(winningNumber) === 'green' && 'bg-green-700'
                )}
                style={{
                  boxShadow: '0 0 30px rgba(212, 175, 55, 0.6)',
                }}
              >
                {winningNumber}
              </motion.div>

              {/* Result message */}
              <motion.h2
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className={cn(
                  'font-cinzel text-3xl font-bold mb-4',
                  lastWinnings > 0 ? 'text-casino-gold' : 'text-gray-400'
                )}
              >
                {lastWinnings > 0 ? '¡GANASTE!' : 'SIN SUERTE'}
              </motion.h2>

              {/* Winnings */}
              {lastWinnings > 0 && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.4 }}
                  className="mb-6"
                >
                  <span className="text-casino-gold/60 font-cinzel">GANANCIA: </span>
                  <span className="text-4xl font-bold text-casino-gold">
                    ${lastWinnings.toLocaleString()}
                  </span>

                  {/* Winning bets breakdown */}
                  {winningBets.length > 0 && (
                    <div className="mt-4 text-sm text-gray-400">
                      <p className="mb-1">Apuestas ganadoras:</p>
                      {winningBets.map((bet, i) => (
                        <p key={i}>
                          {bet.type === 'straight'
                            ? `Número ${bet.numbers[0]}`
                            : bet.type}
                          : ${bet.amount} → $
                          {calculateTotalWinnings([bet], winningNumber)}
                        </p>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}

              {lastWinnings === 0 && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="text-gray-400 mb-6"
                >
                  Perdiste ${totalBet.toLocaleString()} esta ronda
                </motion.p>
              )}

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <Button
                  variant="gold"
                  onClick={handleNewRound}
                  className="px-12 py-4 font-cinzel text-xl"
                >
                  NUEVA RONDA
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
